/**
 * SegmentationAnnotationForm — Config-driven attribute form for segmentation annotations
 *
 * Renders form fields dynamically from a SegmentationTypeSchema definition.
 * - `select` fields render as radio button groups (faster for short option lists)
 * - `boolean` fields render as checkboxes
 *
 * Also displays read-only derived measurements (volume, axes, HU stats) when available.
 */

import React from "react";
import type {
  SegmentationTypeSchema,
  AnnotationFieldSchema,
  AnnotationValue,
} from "../../config/annotationSchemas";
import { formatOptionLabel } from "../../config/annotationSchemas";
import type { DerivedMeasurements } from "../../atoms/segmentationAtoms";

interface SegmentationAnnotationFormProps {
  schema: SegmentationTypeSchema;
  values: Record<string, AnnotationValue>;
  onChange: (field: string, value: AnnotationValue) => void;
  measurements: DerivedMeasurements | null;
  disabled?: boolean;
  /** When true, render only `light` fields (the quick-capture set). Default full. */
  lightMode?: boolean;
}

function SelectField({
  fieldKey,
  field,
  value,
  onChange,
  disabled,
}: {
  fieldKey: string;
  field: AnnotationFieldSchema;
  value: string | undefined;
  onChange: (field: string, value: string) => void;
  disabled?: boolean;
}) {
  return (
    <div className="space-y-1">
      <label className="text-[11px] font-medium text-fg-muted uppercase tracking-wider">
        {field.display_name}
        {field.required && <span className="text-fg ml-0.5">*</span>}
      </label>
      <div className="flex flex-wrap gap-1">
        {field.options?.map((option) => (
          <button
            key={option}
            type="button"
            disabled={disabled}
            onClick={() => onChange(fieldKey, option)}
            className={`px-2 py-1 text-[10px] rounded-md border transition-all duration-150 ${
              value === option
                ? "bg-primary/20 border-primary/50 text-primary-foreground font-semibold"
                : "border-white/10 text-fg-muted hover:border-white/20 hover:text-fg"
            } disabled:opacity-30 disabled:cursor-not-allowed`}
          >
            {formatOptionLabel(option)}
          </button>
        ))}
      </div>
    </div>
  );
}

function MultiSelectField({
  fieldKey,
  field,
  value,
  onChange,
  disabled,
}: {
  fieldKey: string;
  field: AnnotationFieldSchema;
  value: string[];
  onChange: (field: string, value: string[]) => void;
  disabled?: boolean;
}) {
  const toggle = (option: string) => {
    const next = value.includes(option) ? value.filter((v) => v !== option) : [...value, option];
    onChange(fieldKey, next);
  };
  return (
    <div className="space-y-1">
      <label className="text-[11px] font-medium text-fg-muted uppercase tracking-wider">
        {field.display_name}
        {field.required && <span className="text-fg ml-0.5">*</span>}
      </label>
      <div className="flex flex-wrap gap-1">
        {field.options?.map((option) => (
          <button
            key={option}
            type="button"
            disabled={disabled}
            onClick={() => toggle(option)}
            className={`px-2 py-1 text-[10px] rounded-md border transition-all duration-150 ${
              value.includes(option)
                ? "bg-primary/20 border-primary/50 text-primary-foreground font-semibold"
                : "border-white/10 text-fg-muted hover:border-white/20 hover:text-fg"
            } disabled:opacity-30 disabled:cursor-not-allowed`}
          >
            {formatOptionLabel(option)}
          </button>
        ))}
      </div>
    </div>
  );
}

function BooleanField({
  fieldKey,
  field,
  value,
  onChange,
  disabled,
}: {
  fieldKey: string;
  field: AnnotationFieldSchema;
  value: boolean;
  onChange: (field: string, value: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <label className="flex items-center gap-2 cursor-pointer group">
      <input
        type="checkbox"
        checked={value}
        onChange={(e) => onChange(fieldKey, e.target.checked)}
        disabled={disabled}
        className="w-3.5 h-3.5 rounded border-white/20 bg-transparent text-signal-text-tint
                   focus:ring-primary/30 focus:ring-offset-0 disabled:opacity-30"
      />
      <span className="text-[11px] text-fg group-hover:text-white transition-colors">
        {field.display_name}
        {field.required && <span className="text-fg ml-0.5">*</span>}
      </span>
    </label>
  );
}

function NumberField({
  fieldKey,
  field,
  value,
  onChange,
  disabled,
}: {
  fieldKey: string;
  field: AnnotationFieldSchema;
  value: number | undefined;
  onChange: (field: string, value: number) => void;
  disabled?: boolean;
}) {
  return (
    <div className="space-y-1">
      <label className="text-[11px] font-medium text-fg-muted uppercase tracking-wider">
        {field.display_name}
        {field.required && <span className="text-fg ml-0.5">*</span>}
      </label>
      <div className="flex items-center gap-1.5">
        <input
          type="number"
          inputMode="decimal"
          value={value === undefined || Number.isNaN(value) ? "" : value}
          disabled={disabled}
          onChange={(e) =>
            onChange(fieldKey, e.target.value === "" ? NaN : parseFloat(e.target.value))
          }
          className="w-24 px-2 py-1 text-[11px] rounded-md border border-white/10 bg-transparent text-fg tabular-nums
                     focus:border-primary/50 focus:outline-none disabled:opacity-30 disabled:cursor-not-allowed"
        />
        {field.unit && <span className="text-[10px] text-fg-muted">{field.unit}</span>}
      </div>
    </div>
  );
}

function MeasurementsDisplay({ measurements }: { measurements: DerivedMeasurements }) {
  const fmt = (v: number, decimals = 1) => (v === 0 ? "—" : v.toFixed(decimals));

  return (
    <div className="space-y-1">
      <div className="text-[11px] font-medium text-fg-muted uppercase tracking-wider">
        Measurements (estimate)
      </div>
      <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-[10px]">
        <span className="text-fg-muted">Volume</span>
        <span className="text-fg tabular-nums">{fmt(measurements.volume_mm3, 0)} mm³</span>
        <span className="text-fg-muted">Long axis</span>
        <span className="text-fg tabular-nums">{fmt(measurements.long_axis_mm)} mm</span>
        <span className="text-fg-muted">Short axis</span>
        <span className="text-fg tabular-nums">{fmt(measurements.short_axis_mm)} mm</span>
        <span className="text-fg-muted">Slices</span>
        <span className="text-fg tabular-nums">{measurements.slice_count}</span>
        <span className="text-fg-muted">HU range</span>
        <span className="text-fg tabular-nums">
          {measurements.hu_min === 0 && measurements.hu_max === 0
            ? "—"
            : `${fmt(measurements.hu_min, 0)}–${fmt(measurements.hu_max, 0)}`}
        </span>
        <span className="text-fg-muted">HU mean</span>
        <span className="text-fg tabular-nums">{fmt(measurements.hu_avg, 0)}</span>
      </div>
    </div>
  );
}

function FieldRenderer({
  fieldKey,
  field,
  values,
  onChange,
  disabled,
}: {
  fieldKey: string;
  field: AnnotationFieldSchema;
  values: Record<string, AnnotationValue>;
  onChange: (field: string, value: AnnotationValue) => void;
  disabled?: boolean;
}) {
  // Show the same conditional-required state that full-view completion uses.
  // This keeps the asterisk useful for fields such as rectal_height when
  // colon_segment=rectum, including fields that are not part of light view.
  const effectiveField = {
    ...field,
    required: isFieldRequired(field, values),
  };
  if (field.type === "multi_select") {
    return (
      <MultiSelectField
        fieldKey={fieldKey}
        field={effectiveField}
        value={Array.isArray(values[fieldKey]) ? (values[fieldKey] as string[]) : []}
        onChange={onChange}
        disabled={disabled}
      />
    );
  }
  if (field.type === "number") {
    return (
      <NumberField
        fieldKey={fieldKey}
        field={effectiveField}
        value={typeof values[fieldKey] === "number" ? (values[fieldKey] as number) : undefined}
        onChange={onChange}
        disabled={disabled}
      />
    );
  }
  if (field.type === "boolean") {
    return (
      <BooleanField
        fieldKey={fieldKey}
        field={effectiveField}
        value={!!values[fieldKey]}
        onChange={onChange}
        disabled={disabled}
      />
    );
  }
  return (
    <SelectField
      fieldKey={fieldKey}
      field={effectiveField}
      value={values[fieldKey] as string | undefined}
      onChange={onChange}
      disabled={disabled}
    />
  );
}

export function SegmentationAnnotationForm({
  schema,
  values,
  onChange,
  measurements,
  disabled,
  lightMode = false,
}: SegmentationAnnotationFormProps) {
  // Light mode shows only the quick-capture (`light`) fields; full view shows all.
  const fieldEntries = Object.entries(schema.fields).filter(([, f]) => !lightMode || f.light);
  const hasGroups = schema.field_groups && schema.field_groups.length > 0;

  return (
    <div className="space-y-3">
      {/* Measurements (read-only, shown when segment has been drawn) */}
      {measurements && measurements.volume_mm3 > 0 && (
        <MeasurementsDisplay measurements={measurements} />
      )}

      {hasGroups ? (
        // Grouped layout — render fields under group headings, with conditional visibility
        <>
          {schema.field_groups!.map((groupName) => {
            const groupFields = fieldEntries.filter(([, f]) => f.group === groupName);
            if (groupFields.length === 0) return null;

            // Check group_visibility rules
            const visRule = schema.group_visibility?.[groupName];
            if (visRule) {
              const controlValue = values[visRule.field];
              if (!controlValue || !visRule.values.includes(controlValue as string)) {
                return null; // Hide this group
              }
            }

            return (
              <div key={groupName} className="space-y-2">
                <div className="text-[10px] font-semibold text-fg-muted uppercase tracking-wider border-b border-white/5 pb-1 mt-1">
                  {groupName}
                </div>
                {groupFields.map(([key, field]) => (
                  <FieldRenderer
                    key={key}
                    fieldKey={key}
                    field={field}
                    values={values}
                    onChange={onChange}
                    disabled={disabled}
                  />
                ))}
              </div>
            );
          })}
          {/* Render any ungrouped fields at the end */}
          {fieldEntries
            .filter(([, f]) => !f.group)
            .map(([key, field]) => (
              <FieldRenderer
                key={key}
                fieldKey={key}
                field={field}
                values={values}
                onChange={onChange}
                disabled={disabled}
              />
            ))}
        </>
      ) : (
        // Flat layout — render all fields sequentially (existing behavior)
        fieldEntries.map(([key, field]) => (
          <FieldRenderer
            key={key}
            fieldKey={key}
            field={field}
            values={values}
            onChange={onChange}
            disabled={disabled}
          />
        ))
      )}
    </div>
  );
}

/**
 * Check if all required fields in the schema are filled. Always true in the
 * light (default) view — see below.
 */
export function isFormComplete(
  schema: SegmentationTypeSchema,
  values: Record<string, AnnotationValue>,
  lightMode = false,
): boolean {
  // Light view is quick-capture: NO attribute ever blocks Save. Required fields
  // are enforced only in the full view. Without this, the light view of a schema
  // whose fields are all `required: true` (e.g. renal_stone / "Urinary Stone")
  // gates Save on four picks, which defeats the point of the quick-capture view.
  if (lightMode) return true;

  return Object.entries(schema.fields).every(([key, field]) => {
    const conditionallyRequired = isFieldRequired(field, values);
    if (!conditionallyRequired) return true;

    // Unconditional non-light fields are optional detail fields. An active
    // required_when field is different: full view reveals and enforces it,
    // while the early lightMode return above keeps quick capture nonblocking.
    if (!field.light && !field.required_when) return true;

    // Skip validation for fields in hidden groups
    if (field.group && schema.group_visibility?.[field.group]) {
      const visRule = schema.group_visibility[field.group];
      const controlValue = values[visRule.field];
      if (!controlValue || !visRule.values.includes(controlValue as string)) {
        return true; // Group is hidden, skip validation
      }
    }

    const v = values[key];
    if (field.type === "boolean") return v !== undefined;
    if (field.type === "number") return typeof v === "number" && !Number.isNaN(v);
    if (field.type === "multi_select") return Array.isArray(v) && v.length > 0;
    return v !== undefined && v !== "";
  });
}

/**
 * Mirror backend `required_when` matching. A dependency may be scalar or a
 * multi-select array; any overlap with an allowed value activates the field.
 */
export function isFieldRequired(
  field: AnnotationFieldSchema,
  values: Record<string, AnnotationValue>,
): boolean {
  if (field.required) return true;
  return Object.entries(field.required_when ?? {}).some(([dependency, allowed]) => {
    const value = values[dependency];
    if (Array.isArray(value)) return value.some((item) => allowed.includes(item));
    return typeof value === "string" && allowed.includes(value);
  });
}
